

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createLeaveType } from "../../redux/actions/leaveTypeActions";
import { FiPlus, FiCheck, FiArrowLeft, FiType, FiFileText, FiCalendar } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateLeaveType = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { creating, createError } = useSelector((state) => state.leaveType);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    total_leaves: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.total_leaves) {
      return;
    }

    // Send the entire formData object to the action
    dispatch(createLeaveType(formData));
  };

  // Reset form when creation is successful and we're not in loading state
  React.useEffect(() => {
    if (!creating && !createError) {
      // Check if we should reset based on your business logic
      // You might want to reset only after successful creation
      setFormData({
        name: "",
        description: "", 
        total_leaves: ""
      });
    }
  }, [creating, createError]);

  const isFormValid = formData.name.trim() !== "" && formData.total_leaves > 0;

  if (creating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Creating Leave Type...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/view-leave-types")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Leave Types
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiPlus className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Leave Type
                </h1>
                <p className="text-cyan-100">
                  Add a new type of leave for your organization
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {!createError && !creating && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Leave type created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      The new leave type has been added to the system.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {createError && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiPlus className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {typeof createError === "string" ? createError : "Something went wrong"}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      Please check that all fields are filled correctly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiType className="text-sm" />
                      Leave Type Name <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Enter leave type name (e.g., Sick Leave, Vacation, etc.)"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This will be the primary identifier for the leave type
                  </p>
                </div>

                {/* Total Leaves */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiCalendar className="text-sm" />
                      Total Leaves <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.total_leaves}
                    onChange={(e) => handleChange("total_leaves", e.target.value)}
                    placeholder="Enter total leaves per year"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Number of leaves allocated per year
                  </p>
                </div>

                {/* Description */}
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-sm" />
                      Description
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Enter description (optional) - you can include details about usage, policies, or requirements..."
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-500 dark:placeholder-gray-400 h-24 resize-none"
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Optional: Provide additional context or guidelines for this leave type
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      name: "",
                      description: "",
                      total_leaves: ""
                    });
                  }}
                  disabled={creating}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Form
                </button>
                
                <button
                  type="submit"
                  disabled={creating || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {creating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Leave Type...
                    </>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Create Leave Type
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-cyan-900 dark:text-cyan-100 mb-3">
            💡 Leave Type Guidelines
          </h3>
          <ul className="text-cyan-800 dark:text-cyan-200 text-sm space-y-2">
            <li>• Use clear and descriptive names that employees will easily understand</li>
            <li>• Set appropriate total leaves based on your organization's policy</li>
            <li>• Common allocations: Sick Leave (12), Vacation (15-30), Personal Days (5-10)</li>
            <li>• Consider local labor laws when setting leave allocations</li>
            <li>• Descriptions help employees understand when and how to use each leave type</li>
            <li>• You can always edit or deactivate leave types later if needed</li>
            <li>• Fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateLeaveType;